import { Button, Dialog, IconButton, useToast } from "@stagvista/ui";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Plus,
  Printer,
  Save,
  Trash2,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import * as React from "react";

import { useCloudSession } from "../hooks/use-cloud-session.js";
import { CloudApiError } from "../lib/cloud-api-client.js";
import {
  changeCount,
  computeDraftChanges,
  type DraftFields,
  type NewRow,
} from "../lib/schedule-draft.js";

export const Route = createFileRoute("/services/$serviceId/")({
  component: ScheduleTablePage,
});

/** `apps/cloud-api`'s `Service` response shape — only the fields this page reads. */
interface ServiceDto {
  readonly id: string;
  readonly name: string;
}

/** `apps/cloud-api`'s `ScheduleItem` response shape — only the fields this page reads/writes. */
interface ItemDto {
  readonly id: string;
  readonly title: string;
  readonly speaker: string | null;
  readonly category: string | null;
  readonly durationMs: number;
  readonly position: string;
}

const INPUT_CLASS =
  "w-full rounded border border-transparent bg-transparent px-1 py-1 text-body text-fg-primary hover:border-line focus:border-brand-primary focus:outline-none";

let newRowCounter = 0;
function blankRow(): NewRow {
  newRowCounter += 1;
  return { key: `new-${newRowCounter}`, title: "", speaker: "", category: "", durationMin: "" };
}

function savedFields(item: ItemDto): DraftFields {
  return {
    title: item.title,
    speaker: item.speaker ?? "",
    category: item.category ?? "",
    durationMin: String(item.durationMs / 60_000),
  };
}

function ScheduleTablePage(): React.ReactElement {
  const { session } = useCloudSession();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!session) void navigate({ to: "/sign-in" });
  }, [session, navigate]);

  if (!session) return <></>;
  return <ScheduleTableView />;
}

function ScheduleTableView(): React.ReactElement {
  const { serviceId } = useParams({ from: "/services/$serviceId/" });
  const { client } = useCloudSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [service, setService] = React.useState<ServiceDto | null>(null);
  const [items, setItems] = React.useState<ItemDto[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [edits, setEdits] = React.useState<Record<string, Partial<DraftFields>>>({});
  const [newRows, setNewRows] = React.useState<NewRow[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const [exporting, setExporting] = React.useState<"csv" | "pdf" | null>(null);

  const changes = React.useMemo(
    () => computeDraftChanges(items ?? [], edits, newRows),
    [items, edits, newRows],
  );
  const pending = changeCount(changes);
  const dirty = pending > 0 || changes.problems.length > 0;

  const load = React.useCallback(async () => {
    try {
      const [loadedService, loadedItems] = await Promise.all([
        client.request<ServiceDto>("GET", `/v1/services/${serviceId}`),
        client.request<ItemDto[]>("GET", `/v1/services/${serviceId}/items`),
      ]);
      setService(loadedService);
      setItems(loadedItems.slice().sort((a, b) => (a.position < b.position ? -1 : 1)));
    } catch (err) {
      setLoadError(
        err instanceof CloudApiError
          ? (err.problem.detail ?? err.problem.title)
          : "Couldn't load this schedule.",
      );
    }
  }, [client, serviceId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // The browser's own "leave site?" prompt for a tab close or reload with unsaved work.
  React.useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function reportError(err: unknown, fallback: string): void {
    const message =
      err instanceof CloudApiError ? (err.problem.detail ?? err.problem.title) : fallback;
    toast({ tone: "error", title: fallback, description: message });
  }

  function setEdit(itemId: string, field: keyof DraftFields, value: string): void {
    setEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
  }

  function setNewRowField(key: string, field: keyof DraftFields, value: string): void {
    setNewRows((rows) => rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  }

  function discard(): void {
    setEdits({});
    setNewRows([]);
  }

  async function save(): Promise<void> {
    if (saving) return;
    if (changes.problems.length > 0) {
      toast({
        tone: "error",
        title: "Can't save yet",
        description: changes.problems.join(" "),
      });
      return;
    }
    if (pending === 0) return;

    setSaving(true);
    let saved = 0;
    let failed = 0;
    let lastError: unknown = null;
    const remainingEdits = { ...edits };
    const failedKeys = new Set<string>();

    for (const patch of changes.patches) {
      try {
        await client.request("PATCH", `/v1/items/${patch.id}`, patch.body);
        delete remainingEdits[patch.id];
        saved += 1;
      } catch (err) {
        failed += 1;
        lastError = err;
      }
    }
    // Sequential, in the order shown, so the new rows land in the order they were typed.
    for (const create of changes.creates) {
      try {
        await client.request("POST", `/v1/services/${serviceId}/items`, create.body);
        saved += 1;
      } catch (err) {
        failed += 1;
        failedKeys.add(create.key);
        lastError = err;
      }
    }

    setEdits(remainingEdits);
    // Keep what failed (and rows still being filled in); drop what was created.
    setNewRows((rows) =>
      rows.filter(
        (row) =>
          failedKeys.has(row.key) || !changes.creates.some((create) => create.key === row.key),
      ),
    );
    await load();
    setSaving(false);

    if (failed === 0) {
      toast({
        tone: "success",
        title: "Saved",
        description: `${saved} change${saved === 1 ? "" : "s"} saved and syncing.`,
      });
    } else {
      reportError(lastError, `${failed} change${failed === 1 ? "" : "s"} couldn't be saved`);
    }
  }

  // Ctrl/Cmd+S saves. Held in a ref so the listener always sees the latest edits.
  const saveRef = React.useRef(save);
  saveRef.current = save;
  React.useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        void saveRef.current();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  async function handleDelete(itemId: string): Promise<void> {
    try {
      await client.request("DELETE", `/v1/items/${itemId}`);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      await load();
    } catch (err) {
      reportError(err, "Couldn't delete item");
    }
  }

  async function handleMove(itemId: string, direction: "up" | "down"): Promise<void> {
    if (!items) return;
    const index = items.findIndex((item) => item.id === itemId);
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (index === -1 || targetIndex < 0 || targetIndex >= items.length) return;

    const body =
      direction === "up"
        ? { beforeId: items[targetIndex - 1]?.id, afterId: items[targetIndex]!.id }
        : { beforeId: items[targetIndex]!.id, afterId: items[targetIndex + 1]?.id };

    try {
      await client.request("POST", `/v1/items/${itemId}/move`, body);
      await load();
    } catch (err) {
      reportError(err, "Couldn't reorder item");
    }
  }

  async function handleExport(format: "csv" | "pdf"): Promise<void> {
    setExporting(format);
    try {
      const blob = await client.requestBlob(
        "GET",
        `/v1/services/${serviceId}/export?format=${format}`,
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `schedule-${serviceId}.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      reportError(err, "Couldn't export the schedule");
    } finally {
      setExporting(null);
    }
  }

  function goBack(): void {
    if (dirty) setConfirmLeave(true);
    else void navigate({ to: "/" });
  }

  const rowCount = (items?.length ?? 0) + newRows.length;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-h1 text-fg-primary">{service?.name ?? "Schedule"}</h1>
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeft className="size-4" />
          Back to events
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            void navigate({ to: "/services/$serviceId/import", params: { serviceId } })
          }
        >
          <Upload className="size-4" />
          Import CSV
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={exporting === "csv"}
          onClick={() => void handleExport("csv")}
        >
          Export CSV
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={exporting === "pdf"}
          onClick={() => void handleExport("pdf")}
        >
          Export PDF
        </Button>
        <Button variant="ghost" size="sm" onClick={() => window.print()}>
          <Printer className="size-4" />
          Print
        </Button>

        <div className="ml-auto flex items-center gap-2">
          {dirty ? (
            <span className="text-caption text-status-warning" role="status">
              {pending > 0
                ? `${pending} unsaved change${pending === 1 ? "" : "s"}`
                : "Unsaved changes"}
            </span>
          ) : null}
          {dirty ? (
            <Button variant="ghost" size="sm" disabled={saving} onClick={discard}>
              <Undo2 className="size-4" />
              Discard
            </Button>
          ) : null}
          <Button size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
            <Save className="size-4" />
            Save
          </Button>
        </div>
      </div>

      {loadError ? (
        <p className="text-caption text-status-critical print:hidden">{loadError}</p>
      ) : null}

      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full border-collapse text-body">
          <thead>
            <tr className="border-b border-line bg-surface-overlay text-left text-caption text-fg-tertiary">
              <th className="w-10 px-2 py-2 print:hidden">#</th>
              <th className="px-2 py-2">Title</th>
              <th className="px-2 py-2">Speaker</th>
              <th className="w-24 px-2 py-2">Min</th>
              <th className="px-2 py-2">Category</th>
              <th className="w-28 px-2 py-2 print:hidden">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td className="p-4 text-caption text-fg-tertiary" colSpan={6}>
                  Loading…
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <ItemRow
                  key={item.id}
                  index={index}
                  item={item}
                  values={{ ...savedFields(item), ...edits[item.id] }}
                  isFirst={index === 0}
                  isLast={index === items.length - 1}
                  onChange={(field, value) => setEdit(item.id, field, value)}
                  onDelete={() => handleDelete(item.id)}
                  onMoveUp={() => handleMove(item.id, "up")}
                  onMoveDown={() => handleMove(item.id, "down")}
                />
              ))
            )}
            {items !== null
              ? newRows.map((row, index) => (
                  <tr
                    key={row.key}
                    className="border-t border-line-subtle bg-brand-primary/5 print:hidden"
                  >
                    <td className="px-2 py-1.5 text-caption text-fg-tertiary">
                      {items.length + index + 1}
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        className={INPUT_CLASS}
                        placeholder="Title"
                        aria-label="New row title"
                        autoFocus={index === newRows.length - 1}
                        value={row.title}
                        onChange={(e) => setNewRowField(row.key, "title", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        className={INPUT_CLASS}
                        placeholder="Speaker"
                        aria-label="New row speaker"
                        value={row.speaker}
                        onChange={(e) => setNewRowField(row.key, "speaker", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        className={INPUT_CLASS}
                        placeholder="5"
                        inputMode="decimal"
                        aria-label="New row minutes"
                        value={row.durationMin}
                        onChange={(e) => setNewRowField(row.key, "durationMin", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        className={INPUT_CLASS}
                        placeholder="Category"
                        aria-label="New row category"
                        value={row.category}
                        onChange={(e) => setNewRowField(row.key, "category", e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <IconButton
                        aria-label="Remove this new row"
                        onClick={() => setNewRows((rows) => rows.filter((r) => r.key !== row.key))}
                      >
                        <X />
                      </IconButton>
                    </td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>

        {items !== null ? (
          <div className="border-t border-line-subtle px-2 py-2 print:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNewRows((rows) => [...rows, blankRow()])}
            >
              <Plus className="size-4" />
              Add row
            </Button>
            {rowCount === 0 ? (
              <span className="ml-2 text-caption text-fg-tertiary">
                No rows yet — add one, then press Save.
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <Dialog
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        title="Discard unsaved changes?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmLeave(false)}>
              Keep editing
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmLeave(false);
                discard();
                void navigate({ to: "/" });
              }}
            >
              Discard and leave
            </Button>
          </>
        }
      >
        <p className="text-body text-fg-secondary">
          You have changes that haven&apos;t been saved, so they haven&apos;t synced yet. Leaving
          now will lose them.
        </p>
      </Dialog>
    </div>
  );
}

function ItemRow({
  index,
  item,
  values,
  isFirst,
  isLast,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  item: ItemDto;
  /** What the row shows: the saved value, or the unsaved edit over it. */
  values: DraftFields;
  isFirst: boolean;
  isLast: boolean;
  onChange: (field: keyof DraftFields, value: string) => void;
  onDelete: () => Promise<void>;
  onMoveUp: () => Promise<void>;
  onMoveDown: () => Promise<void>;
}): React.ReactElement {
  const [busy, setBusy] = React.useState(false);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-t border-line-subtle">
      <td className="px-2 py-1.5 text-caption text-fg-tertiary">{index + 1}</td>
      <td className="px-2 py-1.5">
        <input
          className={INPUT_CLASS}
          aria-label={`Title of row ${index + 1}`}
          value={values.title}
          onChange={(e) => onChange("title", e.target.value)}
        />
      </td>
      <td className="px-2 py-1.5">
        <input
          className={INPUT_CLASS}
          aria-label={`Speaker of row ${index + 1}`}
          value={values.speaker}
          onChange={(e) => onChange("speaker", e.target.value)}
        />
      </td>
      <td className="px-2 py-1.5">
        <input
          className={INPUT_CLASS}
          inputMode="decimal"
          aria-label={`Minutes of row ${index + 1}`}
          value={values.durationMin}
          onChange={(e) => onChange("durationMin", e.target.value)}
        />
      </td>
      <td className="px-2 py-1.5">
        <input
          className={INPUT_CLASS}
          aria-label={`Category of row ${index + 1}`}
          value={values.category}
          onChange={(e) => onChange("category", e.target.value)}
        />
      </td>
      <td className="px-2 py-1.5 print:hidden">
        <div className="flex items-center gap-0.5">
          <IconButton
            aria-label="Move up"
            disabled={isFirst || busy}
            onClick={() => void run(onMoveUp)}
          >
            <ChevronUp />
          </IconButton>
          <IconButton
            aria-label="Move down"
            disabled={isLast || busy}
            onClick={() => void run(onMoveDown)}
          >
            <ChevronDown />
          </IconButton>
          <IconButton
            aria-label={`Delete ${item.title}`}
            disabled={busy}
            onClick={() => void run(onDelete)}
          >
            <Trash2 />
          </IconButton>
        </div>
      </td>
    </tr>
  );
}
